'use client';

import { c as _c } from "react/compiler-runtime";
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';
import { useOutcomeTokenSnapshot } from '@/hooks/gifts/use-outcome-token-snapshot';
import { useFormattedCurrency } from '@/lib/currency';
import { estimateOutcomeTokenSpotValueFromStringsD6 } from '@/lib/gifts/outcome-token-spot-value';
const ZERO_BIG: bigint = BigInt('0');

/**
 * Minimal shape consumed by `<GiftsTotalAmount />`. Both `GiftRowData` (lists)
 * and `VaultContributor` (claim flow) project into this with a small adapter.
 */
export interface GiftAmountInput {
  /** Stable React key — opaqueId, depositPubkey, or composite. */
  key: string;
  assetKind: 'usdc' | 'outcome_token';
  /** Base units (D6 for USDC and the markets program's outcome mints). */
  amount: string;
  /** Required when assetKind === 'outcome_token'. */
  mintPubkey?: string | null;
}
type ValueMap = Readonly<Record<string, string | null>>;
interface TokenValueProbeProps {
  probeKey: string;
  mintPubkey: string;
  amountBaseUnits: string;
  setValues: Dispatch<SetStateAction<ValueMap>>;
}

/**
 * Invisible per-token-row driver. Subscribes one outcome-token snapshot,
 * computes its D6 USDC value, and reports it back to the aggregator.
 *
 * Why a probe instead of looping `useOutcomeTokenSnapshot` in the parent?
 * Hooks must run in stable order across renders, so we can't iterate the
 * (potentially variable-length) row list inside a single component. Each
 * probe component owns exactly one snapshot subscription.
 */
function TokenValueProbe(props) {
  const $ = _c(10);
  const snapshot = useOutcomeTokenSnapshot(props.mintPubkey);
  let t0;
  bb0: {
    if (snapshot.currentPriceD9 === null) {
      t0 = null;
      break bb0;
    }
    let t1;
    if ($[0] !== props.amountBaseUnits || $[1] !== snapshot.currentPriceD9) {
      t1 = estimateOutcomeTokenSpotValueFromStringsD6(props.amountBaseUnits, snapshot.currentPriceD9)?.toString() ?? null;
      $[0] = props.amountBaseUnits;
      $[1] = snapshot.currentPriceD9;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    t0 = t1;
  }
  const serialized = t0;
  let t1;
  if ($[3] !== props || $[4] !== serialized) {
    t1 = () => {
      props.setValues(prev => {
        const current = prev[props.probeKey] ?? null;
        if (current === serialized) {
          return prev;
        }
        return {
          ...prev,
          [props.probeKey]: serialized
        };
      });
    };
    $[3] = props;
    $[4] = serialized;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props.probeKey || $[7] !== props.setValues || $[8] !== serialized) {
    t2 = [props.probeKey, serialized, props.setValues];
    $[6] = props.probeKey;
    $[7] = props.setValues;
    $[8] = serialized;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  useEffect(t1, t2);
  return null;
}
export interface GiftsTotalAmountProps {
  rows: ReadonlyArray<GiftAmountInput>;
  format?: 'compact' | 'full' | 'short';
  className?: string;
  testId?: string;
}

/**
 * Renders a single combined USDC estimate across a mixed list of cash + outcome
 * token gifts. Token rows resolve their marginal-spot value via the existing
 * `useOutcomeTokenSnapshot` hook (one probe per row); the displayed total
 * updates in place once each snapshot arrives. Mixed/token totals carry a `~`
 * prefix because they are not liquidation quotes.
 *
 * USDC-only callers receive a fully-resolved total on first render — no probes
 * are mounted. While token snapshots are still loading, the displayed value
 * reflects the cash sum plus all already-resolved tokens.
 */
export function GiftsTotalAmount(props: GiftsTotalAmountProps): React.ReactNode {
  const {
    formatD6
  } = useFormattedCurrency();
  const [tokenValues, setTokenValues] = useState<ValueMap>({});
  let usdcD6: bigint = ZERO_BIG;
  for (const r of props.rows) {
    if (r.assetKind === 'usdc') {
      try {
        usdcD6 = usdcD6 + BigInt(r.amount.length === 0 ? '0' : r.amount);
      } catch {
        // SILENT-CATCH: amount unparseable — skip rather than crash the total.
      }
    }
  }
  const tokens: {
    key: string;
    mintPubkey: string;
    amount: string;
  }[] = props.rows.filter((r_0): r_0 is GiftAmountInput & {
    mintPubkey: string;
  } => r_0.assetKind === 'outcome_token' && r_0.mintPubkey !== null && r_0.mintPubkey !== undefined && r_0.mintPubkey.length > 0).map(r_1 => ({
    key: r_1.key,
    mintPubkey: r_1.mintPubkey,
    amount: r_1.amount
  }));
  let tokenTotalD6: bigint = ZERO_BIG;
  for (const t of tokens) {
    const v = tokenValues[t.key];
    if (v !== undefined && v !== null) {
      try {
        tokenTotalD6 = tokenTotalD6 + BigInt(v);
      } catch {
        // SILENT-CATCH: stale stringified bigint — skip
      }
    }
  }
  const totalD6: bigint = usdcD6 + tokenTotalD6;
  const formatted = formatD6(totalD6.toString(), props.format ?? 'compact');
  return <>
      {tokens.map((t_0): React.ReactNode => <TokenValueProbe key={t_0.key} probeKey={t_0.key} mintPubkey={t_0.mintPubkey} amountBaseUnits={t_0.amount} setValues={setTokenValues} />)}
      <span className={props.className} data-testid={props.testId}>
        {tokens.length > 0 ? `~${formatted}` : formatted}
      </span>
    </>;
}